import { CalendarEvent, TeamMember } from '@demind-inc/core';

import { PeakDipPhase, PeakDipStartEndSet } from '../../../data-access';
import { getRoundDownDateTime, getRoundUpDateTime } from '../../CalendarView';
import { CalendarTimelineBackgroundEvent, zoneColorForPeaksDipsPhase } from '../../CalendarView';
import { dayjs } from '../../../utils/date-time';

const PEAK_DIP_PHASES: PeakDipPhase[] = [
  'wakeup_low',
  'morning_high',
  'morning_low',
  'evening_high',
  'evening_low',
];

type TeamMemberWithMeta = TeamMember & {
  email?: string;
  displayName?: string;
  avatarUrl?: string;
};
type TeamTFunction = (
  key: string,
  options?: Record<string, unknown> & { defaultValue?: string }
) => string;

type PhaseRange = {
  start: string;
  end: string;
  timezone?: string;
};

const TIMEZONE_OFFSET_REGEX = /(Z|[+-]\d{2}:?\d{2})$/i;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const parseString = (value: unknown) => {
  return typeof value === 'string' ? value.trim() : '';
};

const parsePhaseRange = (value: unknown): PhaseRange | null => {
  if (!isRecord(value)) {
    return null;
  }

  const start =
    parseString(value.start) ||
    parseString(value.startAt) ||
    parseString(value.startTime) ||
    parseString(value.from);
  const end =
    parseString(value.end) ||
    parseString(value.endAt) ||
    parseString(value.endTime) ||
    parseString(value.to);

  if (!start || !end) {
    return null;
  }

  return {
    start,
    end,
    timezone: parseString(value.timezone) || undefined,
  };
};

export const buildTeamMemberLookup = (members: TeamMember[]) => {
  const lookup: Record<string, TeamMember> = {};

  members.forEach((member) => {
    if (member.teamMemberId) {
      lookup[member.teamMemberId] = member;
    }

    if (member.userId) {
      lookup[member.userId] = member;
    }
  });

  return lookup;
};

export const getTeamMemberDisplayName = (
  member: TeamMember | undefined,
  fallbackIndex: number,
  currentUserId: string,
  t?: TeamTFunction
) => {
  const enrichedMember = member as TeamMemberWithMeta | undefined;
  const memberName =
    member?.name?.trim() ||
    enrichedMember?.displayName?.trim() ||
    enrichedMember?.email?.trim() ||
    (t
      ? t('team.common.memberWithIndex', { index: fallbackIndex + 1 })
      : `Member ${fallbackIndex + 1}`);

  return member?.userId === currentUserId
    ? t
      ? t('team.common.youName', { name: memberName })
      : `${memberName} (You)`
    : memberName;
};

export const getTeamMemberPhotoUrl = (member?: TeamMember) => {
  const enrichedMember = member as TeamMemberWithMeta | undefined;
  return member?.photoUrl?.trim() || enrichedMember?.avatarUrl?.trim() || '';
};

export const getTeamMemberInitials = (displayName: string) => {
  const normalizedName = displayName.replace(/\s*\(You\)\s*$/, '').trim();

  if (!normalizedName) {
    return 'TM';
  }

  const nameTokens = normalizedName.split(/\s+/).filter(Boolean);

  if (nameTokens.length >= 2) {
    return `${nameTokens[0][0] || ''}${nameTokens[1][0] || ''}`.toUpperCase();
  }

  return normalizedName.slice(0, 2).toUpperCase();
};

export const normalizeTeamDayPhases = (phases: unknown) => {
  if (!isRecord(phases)) {
    return null;
  }

  const normalizedPhases: Partial<PeakDipStartEndSet> = {};

  PEAK_DIP_PHASES.forEach((phase) => {
    const phaseRange = parsePhaseRange(phases[phase]);

    if (!phaseRange) {
      return;
    }

    normalizedPhases[phase] = phaseRange;
  });

  if (!Object.keys(normalizedPhases).length) {
    return null;
  }

  return normalizedPhases as PeakDipStartEndSet;
};

export const getTeamDayPhaseDateTime = (value: string, timezone?: string) => {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  const dateTime =
    timezone && !TIMEZONE_OFFSET_REGEX.test(normalizedValue)
      ? dayjs.tz(normalizedValue, timezone).local()
      : dayjs(normalizedValue);

  return dateTime.isValid() ? dateTime : null;
};

export const isCancelledTeamDayEvent = (event: Pick<CalendarEvent, 'status' | 'summary'>) => {
  const status = event.status?.toLowerCase();

  if (status === 'cancelled' || status === 'canceled') {
    return true;
  }

  const summary = event.summary?.trim().toLowerCase();
  return summary === 'cancelled' || summary === 'canceled';
};

export const buildTeamDayBackgroundEvents = (
  resourceId: string,
  phases: PeakDipStartEndSet | null,
  timezone?: string
): CalendarTimelineBackgroundEvent[] => {
  if (!phases) {
    return [];
  }

  return PEAK_DIP_PHASES.reduce<CalendarTimelineBackgroundEvent[]>((events, phase) => {
    const phaseRange = phases[phase];

    if (!phaseRange?.start || !phaseRange?.end) {
      return events;
    }

    const phaseTimezone = phaseRange.timezone || timezone;
    const start = getTeamDayPhaseDateTime(phaseRange.start, phaseTimezone);
    const end = getTeamDayPhaseDateTime(phaseRange.end, phaseTimezone);

    if (!start || !end || end.diff(start, 'minutes') <= 0) {
      return events;
    }

    events.push({
      start: getRoundUpDateTime(start).toDate(),
      end: getRoundDownDateTime(end).toDate(),
      color: zoneColorForPeaksDipsPhase[phase],
      resourceId,
    });

    return events;
  }, []);
};
